"use client";
import {PlaybackService, Recording, RecordingsResponse} from "@/app/service/playbackService";
import {useEffectSafe} from "@/app/utils/hooks";
import {useRef, useState, useEffect} from "react";
import {useSearchParams, useRouter} from "next/navigation";
import LoadingScreen from "../components/layout/Loading";
import {useToast} from "../components/ui/Toast";
import FeaturedRecordings from "../components/FeaturedRecordings";
import PageWrapper from "../components/ui/PageWrapper";

const PlaybackPage = () => {
    const [recordings, setRecordings] = useState<Recording[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
    const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
    const [loadingPlayback, setLoadingPlayback] = useState(false);
    const [groupBy, setGroupBy] = useState<'none' | 'displayName' | 'eventKey'>('none');
    const {addToast} = useToast();

    const searchParams = useSearchParams();
    const router = useRouter();

    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<any>(null);

    // Helper function to find recording by URL parameters
    const findRecordingByParams = (recordings: Recording[]) => {
        const recordingParam = searchParams.get('recording'); // compositionArn or recordingPrefix

        if (!recordingParam) {
            return null;
        }

        // Try to find by exact compositionArn or recordingPrefix match
        const recording = recordings.find(r =>
            r.compositionArn === recordingParam ||
            r.recordingPrefix === recordingParam ||
            r.compositionArn.includes(recordingParam) ||
            r.recordingPrefix.includes(recordingParam)
        );

        return recording || null;
    };

    // Update URL when recording is selected
    const updateUrlForRecording = (recording: Recording | null) => {
        const params = new URLSearchParams(searchParams);

        if (recording) {
            // Use compositionArn as the primary identifier
            params.set('recording', recording.compositionArn);
        } else {
            // Remove recording param when no recording is selected
            params.delete('recording');
        }

        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
        router.replace(newUrl, {scroll: false});
    };

    // Load recordings on component mount
    useEffectSafe(() => {
        const loadRecordings = async () => {
            try {
                const response: RecordingsResponse = await PlaybackService.getRecordings();

                setRecordings(response.recordings || []);
            } catch (error) {
                console.error("Failed to load recordings:", error);

                // Handle specific CORS errors
                if (error instanceof TypeError && error.message.includes('CORS')) {
                    addToast({
                        title: "Network error - please check your connection",
                        type: "error"
                    });
                } else {
                    addToast({
                        title: "Failed to load recordings",
                        type: "error"
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        loadRecordings();
    }, []);

    // Auto-select recording based on URL parameters when recordings are loaded
    useEffect(() => {
        if (recordings.length > 0 && !selectedRecording) {
            const recordingFromUrl = findRecordingByParams(recordings);
            if (recordingFromUrl) {
                console.log("Auto-selecting recording from URL:", recordingFromUrl);
                handleRecordingSelect(recordingFromUrl);
            }
        }
    }, [recordings, selectedRecording]);

    const handleRecordingSelect = async (recording: Recording) => {
        if (loadingPlayback) return;

        // Update URL to reflect the selected recording
        updateUrlForRecording(recording);

        // Get the best manifest key for playback
        const manifestKey = PlaybackService.getBestManifestKey(recording);
        if (!manifestKey) {
            addToast({title: "No playback manifest found", type: "error"});
            return;
        }

        setLoadingPlayback(true);
        setSelectedRecording(recording);

        try {
            console.log("Getting playback URL for recording:", manifestKey);
            const response = await PlaybackService.getPlaybackUrl(manifestKey);
            setPlaybackUrl(response.playbackUrl);

            // Wait a bit for the video element to be rendered, then initialize player
            setTimeout(() => {
                initializePlayer(response.playbackUrl);
            }, 100);
        } catch (error) {
            console.error("Failed to get playback URL:", error);
            addToast({title: "Failed to load recording", type: "error"});
            setSelectedRecording(null);
            setPlaybackUrl(null);
            updateUrlForRecording(null); // Clear URL params on error
        } finally {
            setLoadingPlayback(false);
        }
    };

    const initializePlayer = (url: string, retryCount: number = 0) => {
        console.log("Attempting to initialize player with URL:", url);
        console.log("Video element available:", !!videoRef.current);
        console.log("Retry count:", retryCount);

        // Check if video element is available
        if (!videoRef.current) {
            if (retryCount < 3) {
                console.log(`Video element not ready, retrying in ${100 * (retryCount + 1)}ms...`);
                setTimeout(() => {
                    initializePlayer(url, retryCount + 1);
                }, 100 * (retryCount + 1));
                return;
            } else {
                console.error("Video element not available after retries");
                addToast({title: "Video element not available", type: "error"});
                return;
            }
        }

        console.log("Video element is ready, proceeding with player initialization");

        // Check if IVS Player is available
        if (typeof window === "undefined" || !window.IVSPlayer) {
            console.error("IVS Player not available");
            addToast({title: "Video player not available", type: "error"});
            return;
        }

        // Clean up existing player
        if (playerRef.current) {
            try {
                playerRef.current.delete();
            } catch (error) {
                console.warn("Error cleaning up previous player:", error);
            }
        }

        try {
            console.log("Creating IVS Player...");

            // Create new IVS player (IVS Player create() method takes no arguments)
            playerRef.current = window.IVSPlayer.create();
            playerRef.current.attachHTMLVideoElement(videoRef.current);

            // Set up event listeners
            if (playerRef.current.addEventListener) {
                // Player state changes
                playerRef.current.addEventListener('PlayerStateChanged', (state: any) => {
                    console.log("IVS Player state changed:", state);
                });

                // Error handling - simplified approach
                playerRef.current.addEventListener('Error', (error: any) => {
                    console.error("IVS Player error:", error);
                    addToast({
                        title: "Playback issue detected",
                        type: "warning"
                    });
                });

                // Duration changed (useful for recordings)
                playerRef.current.addEventListener('DurationChanged', (duration: any) => {
                    console.log("IVS Player duration:", duration);
                });
            }

            // Load the stream
            playerRef.current.load(url);

            console.log("IVS Player initialized successfully");

        } catch (error) {
            console.error("Failed to initialize IVS player:", error);
            addToast({title: "Failed to initialize video player", type: "error"});
        }
    };

    // Get featured recordings (visible and featured)
    const getFeaturedRecordings = () => {
        return recordings.filter(recording => recording.isVisible && recording.isFeatured);
    };

    // Get non-featured visible recordings
    const getNonFeaturedRecordings = () => {
        return recordings.filter(recording => recording.isVisible);
    };

    // Get user info for a group (when grouping by displayName)
    const getUserInfoForGroup = (groupKey: string, recordings: Recording[]) => {
        if (groupBy === 'displayName' && recordings.length > 0) {
            return recordings[0].user;
        }
        return null;
    };

    // Get event info for a group (when grouping by eventKey)
    const getEventInfoForGroup = (groupKey: string, recordings: Recording[]) => {
        if (groupBy === 'eventKey' && recordings.length > 0) {
            return recordings[0].event;
        }
        return null;
    };

    // Cleanup player on component unmount
    useEffect(() => {
        return () => {
            console.log("PlaybackPage unmounting, cleaning up player...");
            if (playerRef.current) {
                try {
                    playerRef.current.delete();
                    playerRef.current = null;
                } catch (error) {
                    console.warn("Error cleaning up player on unmount:", error);
                }
            }
        };
    }, []);

    if (loading) {
        return <LoadingScreen/>;
    }

    const featuredRecordings = getFeaturedRecordings();
    const nonFeaturedRecordings = getNonFeaturedRecordings();

    return (
        <PageWrapper>
            <div className="max-w-6xl mx-auto">
                <div className={`grid gap-8 ${selectedRecording ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}>
                    {/* Video Player - only show when a recording is selected */}
                    {selectedRecording && (
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <div>
                                    {/* Enhanced Recording Header */}
                                    <div className="mb-6">
                                        <div className="flex items-start space-x-4 mb-4">
                                            {/* Event Image */}
                                            {selectedRecording.event?.imageUrl && (
                                                <div className="flex-shrink-0">
                                                    <img
                                                        src={selectedRecording.event.imageUrl}
                                                        alt={selectedRecording.event.description}
                                                        className="w-16 h-16 rounded-lg object-cover shadow-sm"
                                                    />
                                                </div>
                                            )}

                                            {/* Recording Details */}
                                            <div className="flex-1 min-w-0">
                                                <h2 className="text-xl font-semibold text-gray-900 mb-1"
                                                    style={{fontFamily: 'Gendy, sans-serif'}}>
                                                    {selectedRecording.event?.description || 'Event Recording'}
                                                </h2>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    {new Date(selectedRecording.startTime).toLocaleString() || 'Unknown Date'}
                                                </p>

                                                {/* Commentator Info */}
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex items-center text-sm text-gray-700">
                                                        <div className="flex items-center mr-3">
                                                            {selectedRecording.user?.avatarUrl ? (
                                                                <img
                                                                    src={selectedRecording.user.avatarUrl}
                                                                    alt={selectedRecording.user.displayName}
                                                                    className="w-6 h-6 rounded-full mr-2"
                                                                />
                                                            ) : (
                                                                <div
                                                                    className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mr-2">
                                <span className="text-xs font-medium text-white">
                                  {selectedRecording.user?.displayName?.charAt(0).toUpperCase() || 'C'}
                                </span>
                                                                </div>
                                                            )}
                                                            <span className="font-medium">
                              {selectedRecording.user?.displayName || 'Anonymous Commentator'}
                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Event Description or Additional Info */}
                                        {selectedRecording.event?.eventDetails && (
                                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                                                <p>{selectedRecording.event.eventDetails}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Video Player Container */}
                                    <div className="relative bg-black rounded-lg overflow-hidden shadow-lg"
                                         style={{aspectRatio: "16/9"}}>
                                        {playbackUrl ? (
                                            <video
                                                ref={videoRef}
                                                className="w-full h-full"
                                                controls
                                                playsInline
                                                poster={selectedRecording.event?.imageUrl || "/dummy-background.png"}
                                            />
                                        ) : (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="text-white text-center">
                                                    {loadingPlayback ? (
                                                        <div className="flex flex-col items-center space-y-3">
                                                            <div
                                                                className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                                            <span className="text-sm">Preparing your recording...</span>
                                                            <span className="text-xs text-gray-300">This may take a moment</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center space-y-2">
                                                            <div
                                                                className="animate-pulse w-12 h-12 bg-gray-600 rounded-lg"></div>
                                                            <span>Initializing player...</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Recording Metadata Footer */}
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                {selectedRecording.recordingFormat && (
                                                    <span
                                                        className="capitalize">Format: {selectedRecording.recordingFormat}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Recordings List */}
                    <div className={selectedRecording ? "lg:col-span-1" : "max-w-4xl mx-auto"}>
                        <div className="bg-white rounded-lg shadow-sm border p-6">
                            <h2 className="text-xl font-semibold mb-4">
                                {selectedRecording ? "Available Recordings" : "Select a Recording to Watch"}
                            </h2>

                            {/* Grouping Controls */}
                            <div className="mb-4">
                                <span className="text-sm text-gray-500 mr-2">Group by:</span>
                                <button
                                    onClick={() => setGroupBy('none')}
                                    className={`text-sm px-3 py-1 rounded-md mr-2 transition-all ${
                                        groupBy === 'none' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    None
                                </button>
                                <button
                                    onClick={() => setGroupBy('displayName')}
                                    className={`text-sm px-3 py-1 rounded-md mr-2 transition-all ${
                                        groupBy === 'displayName' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    User
                                </button>
                                <button
                                    onClick={() => setGroupBy('eventKey')}
                                    className={`text-sm px-3 py-1 rounded-md transition-all ${
                                        groupBy === 'eventKey' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Event
                                </button>
                            </div>

                            {recordings.filter(r => r.isVisible).length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No recordings available</p>
                                    <p className="text-sm mt-2">Recordings will appear here after events are
                                        completed</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Featured Recordings Section */}
                                    {featuredRecordings.length > 0 && (
                                        <FeaturedRecordings
                                            recordings={featuredRecordings}
                                            selectedRecording={selectedRecording}
                                            onRecordingSelect={handleRecordingSelect}
                                            loadingPlayback={loadingPlayback}
                                        />
                                    )}

                                    {/* Regular Recordings Section */}
                                    {nonFeaturedRecordings.length > 0 && (
                                        <div>
                                            {featuredRecordings.length > 0 && (
                                                <div className="flex items-center mb-3">
                                                    <h3 className="text-sm font-semibold text-gray-700">All
                                                        Recordings</h3>
                                                    <span
                                                        className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {nonFeaturedRecordings.length}
                        </span>
                                                </div>
                                            )}

                                            {/* Apply grouping only to non-featured recordings */}
                                            {(() => {
                                                const nonFeaturedGrouped = (() => {
                                                    if (groupBy === 'none') {
                                                        return {'All Recordings': nonFeaturedRecordings};
                                                    }

                                                    const groups: { [key: string]: Recording[] } = {};
                                                    nonFeaturedRecordings.forEach(recording => {
                                                        let groupKey: string;

                                                        if (groupBy === 'displayName') {
                                                            groupKey = recording.user?.displayName || 'Unknown User';
                                                        } else if (groupBy === 'eventKey') {
                                                            groupKey = recording.event?.description || 'Unknown Event';
                                                        } else {
                                                            groupKey = 'All Recordings';
                                                        }

                                                        if (!groups[groupKey]) {
                                                            groups[groupKey] = [];
                                                        }
                                                        groups[groupKey].push(recording);
                                                    });

                                                    return groups;
                                                })();

                                                return Object.entries(nonFeaturedGrouped).map(([groupKey, recordings], index) => {
                                                    const isUserGroup = groupBy === 'displayName';
                                                    const isEventGroup = groupBy === 'eventKey';
                                                    const userInfo = isUserGroup ? getUserInfoForGroup(groupKey, recordings) : null;
                                                    const eventInfo = isEventGroup ? getEventInfoForGroup(groupKey, recordings) : null;

                                                    return (
                                                        <div key={groupKey}
                                                             className={`${index > 0 || (groupBy !== 'none' && featuredRecordings.length === 0) ? 'border-t border-gray-200 pt-4' : ''}`}>
                                                            {groupBy !== 'none' && (
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <div className="text-sm font-medium">
                                                                        {isUserGroup && userInfo ? (
                                                                            <div className="flex items-center">
                                                                                {userInfo.avatarUrl ? (
                                                                                    <img
                                                                                        src={userInfo.avatarUrl}
                                                                                        alt={userInfo.displayName}
                                                                                        className="w-8 h-8 rounded-full mr-2"
                                                                                    />
                                                                                ) : (
                                                                                    <div
                                                                                        className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2">
                                          <span className="text-xs font-medium text-gray-600">
                                            {userInfo.displayName?.charAt(0).toUpperCase() || 'U'}
                                          </span>
                                                                                    </div>
                                                                                )}
                                                                                <span>{userInfo.displayName}</span>
                                                                            </div>
                                                                        ) : isEventGroup && eventInfo ? (
                                                                            <div className="flex items-center">
                                                                                {eventInfo.imageUrl ? (
                                                                                    <img
                                                                                        src={eventInfo.imageUrl}
                                                                                        alt={eventInfo.eventKey}
                                                                                        className="w-8 h-8 rounded-lg object-cover mr-2"
                                                                                    />
                                                                                ) : null}
                                                                                <span>{eventInfo.description}</span>
                                                                            </div>
                                                                        ) : (
                                                                            <span>{groupKey}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="space-y-3">
                                                                {recordings.map((recording, recordingIndex) => {
                                                                    const recordingName = recording.event?.description || 'Unknown Event';
                                                                    const recordingDate = new Date(recording.startTime).toLocaleString() || 'Unknown Date';
                                                                    const userDisplayName = recording.user?.displayName || 'Anonymous';
                                                                    const userAvatarUrl = recording.user?.avatarUrl;

                                                                    return (
                                                                        <div
                                                                            key={recording.compositionArn || `regular-${recordingIndex}`}
                                                                            className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                                                                selectedRecording?.compositionArn === recording.compositionArn
                                                                                    ? "border-blue-500 bg-blue-50"
                                                                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                                                            }`}
                                                                            onClick={() => handleRecordingSelect(recording)}
                                                                        >
                                                                            <div
                                                                                className="flex items-start justify-between mb-2">
                                                                                <div
                                                                                    className="font-medium text-sm mb-1 flex-1">
                                                                                    {recordingName}
                                                                                </div>
                                                                            </div>

                                                                            {/* User info with avatar and display name - only show when NOT grouped by user */}
                                                                            {groupBy !== 'displayName' && (
                                                                                <div className="flex items-center mb-2">
                                                                                    {userAvatarUrl ? (
                                                                                        <img
                                                                                            src={userAvatarUrl}
                                                                                            alt={userDisplayName}
                                                                                            className="w-6 h-6 rounded-full mr-2"
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mr-2">
                                            <span className="text-xs font-medium text-white">
                                              {userDisplayName.charAt(0).toUpperCase()}
                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                    <span
                                                                                        className="text-sm text-gray-700 font-medium">
                                          {userDisplayName}
                                        </span>
                                                                                </div>
                                                                            )}

                                                                            <div className="text-xs text-gray-500 mb-1">
                                                                                {recordingDate}
                                                                            </div>

                                                                            {recording.event?.imageUrl && (
                                                                                <div className="mt-2">
                                                                                    <img
                                                                                        src={recording.event.imageUrl}
                                                                                        alt={recordingName}
                                                                                        className="w-full h-16 object-cover rounded"
                                                                                    />
                                                                                </div>
                                                                            )}

                                                                            {loadingPlayback && selectedRecording?.compositionArn === recording.compositionArn && (
                                                                                <div className="mt-2">
                                                                                    <div
                                                                                        className="animate-pulse text-xs text-blue-600">Loading...
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </PageWrapper>
    );
};

export default PlaybackPage;
